import React, { useState, useEffect } from "react";
import { db } from "../../firebase/firebase";
import { collection, getDocs } from "firebase/firestore";
import { convertTimestampToDateTime } from "../../utils/ProductStatus";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";

const SoldCSV = () => {
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const inventoryCollection = collection(db, "all-products");
        const inventorySnapshot = await getDocs(inventoryCollection);
        const inventoryList = inventorySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const filtered = inventoryList.filter(
          (item) => item.status && (item.history?.length || 0) > 2
        );

        setInventory(filtered);
        applyFilters(filtered);
      } catch (err) {
        console.error("Error fetching inventory:", err);
      }
    };

    fetchInventory();
  }, []);

  const applyFilters = (data) => {
    let filtered = [...data];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter((item) =>
        Object.values(item).some((val) =>
          String(val).toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    // Apply date range filter
    if (startDate || endDate) {
      filtered = filtered.filter((item) => {
        const billedDate = new Date(convertTimestampToDateTime(item.history?.[2]?.timestamp));
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        if (start && end) {
          return billedDate >= start && billedDate <= end;
        } else if (start) {
          return billedDate >= start;
        } else if (end) {
          return billedDate <= end;
        }
        return true;
      });
    }

    setFilteredInventory(filtered.sort((a, b) => 
      b.history[2].timestamp.seconds - a.history[2].timestamp.seconds
    ));
  };

  useEffect(() => {
    applyFilters(inventory);
  }, [searchQuery, startDate, endDate, inventory]);

  const handleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === filteredInventory.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredInventory.map((item) => item.id));
    }
  };

  const exportToExcel = () => {
    if (selectedItems.length === 0) {
      alert("No rows selected. Please select rows to export.");
      return;
    }

    const dataToExport = filteredInventory
      .filter((item) => selectedItems.includes(item.id))
      .map(({ model, variant, imei_1, imei_2, brand, history, serialNumber }) => ({
        Brand: brand,
        Model: model,
        Variant: variant,
        "IMEI 1": imei_1,
        "IMEI 2": imei_2,
        "Date Billed": convertTimestampToDateTime(history?.[2]?.timestamp),
        "Serial Number": serialNumber,
      }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sold Inventory");
    XLSX.writeFile(workbook, "sold_inventory.xlsx");
  };

  return (
    <div className="bg-white h-screen w-full p-5 overflow-x-auto overflow-y-auto">
      <h1 className="text-xl font-bold mb-4">Sold Inventory</h1>
      
      <div className="mb-4 flex flex-wrap gap-4">
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded w-64"
        />
        
        <div className="flex gap-2 items-center">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded"
          />
          <span>to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded"
          />
        </div>

        <button
          onClick={handleSelectAll}
          className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 w-40"
        >
          {selectedItems.length === filteredInventory.length ? "Deselect All" : "Select All"}
        </button>
        
        <button
          onClick={exportToExcel}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Export Selected to Excel
        </button>
      </div>

      <table className="table-auto w-full border-collapse border border-gray-300">
        <thead>
          <tr className="bg-gray-200">
            <th className="border border-gray-300 p-2">Select</th>
            <th className="border border-gray-300 p-2">Brand</th>
            <th className="border border-gray-300 p-2">Model</th>
            <th className="border border-gray-300 p-2">Variant</th>
            <th className="border border-gray-300 p-2">IMEI 1</th>
            <th className="border border-gray-300 p-2">IMEI 2</th>
            <th className="border border-gray-300 p-2">Date Billed</th>
            <th className="border border-gray-300 p-2">Serial Number</th>
          </tr>
        </thead>
        <tbody>
          {filteredInventory.map((item) => (
            <tr
              key={item.id}
              className={`text-center ${selectedItems.includes(item.id) ? "bg-blue-100" : ""}`}
              onClick={() => handleSelectItem(item.id)}
            >
              <td className="border border-gray-300 p-2 cursor-pointer">
                <input type="checkbox" checked={selectedItems.includes(item.id)} readOnly />
              </td>
              <td className="border border-gray-300 p-2">{item.brand}</td>
              <td className="border border-gray-300 p-2">{item.model}</td>
              <td className="border border-gray-300 p-2">{item.variant}</td>
              <td className="border border-gray-300 p-2">{item.imei_1}</td>
              <td className="border border-gray-300 p-2">{item.imei_2}</td>
              <td className="border border-gray-300 p-2">{convertTimestampToDateTime(item.history?.[2]?.timestamp)}</td>
              <td className="border border-gray-300 p-2">
                <Link to={`/product-details/${item.serialNumber}`} className="text-blue-500 underline">
                  {item.serialNumber}
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SoldCSV;